import { Table, Tbody, Td, Thead, Tr, Tooltip } from '@chakra-ui/react';
import React from 'react';

import {
  monthlySummaryDemoSections,
  DemoSectionFormat,
  LineItem,
  SectionContainer,
  CashBalanceRow,
} from './temp-monthly-summary-demo-data';
import { colors } from 'packages/common-frontend/colors';
import { dollarNumberWithCommasOrDash } from 'packages/common-frontend/utils';

// Used in monthly summary view container
export type MonthSummary = { [account: string]: number };

const getLineItemBalance = (
  lineItem: LineItem,
  monthSummary: MonthSummary,
  cashBalanceRows: CashBalanceRow[],
): number => {
  let balance = 0;
  switch (lineItem.type) {
    case 'accrual':
      balance = monthSummary[lineItem.mapping] || 0;
      break;
    case 'cashBalance':
      // eslint-disable-next-line no-case-declarations
      const cashBalance = cashBalanceRows.find(
        (row) => row.account === lineItem.account,
      );
      if (cashBalance) balance = cashBalance.amount;
      break;
    case 'hardcoded':
      balance = lineItem.value;
      break;
    default:
      throw Error('unknown line item type');
  }

  return balance;
};

const getSectionTotal = (
  section: SectionContainer,
  monthSummary: MonthSummary,
  cashBalanceRows: CashBalanceRow[],
): number => {
  switch (section.type) {
    case 'lineItemsContainer':
      return section.lineItems.reduce<number>(
        (total, lineItem) =>
          total + getLineItemBalance(lineItem, monthSummary, cashBalanceRows),
        0,
      );
    case 'summaryAccrualContainer':
      return monthSummary[section.account] || 0;
    case 'summaryCashContainer':
      return section.amount;
    default:
      throw Error('unknown section type');
  }
};

const getFormattedAmount = (
  amount: number | undefined,
  showZero: boolean = false,
): string => {
  let balance = 0;
  if (amount) balance = Math.abs(amount);
  if (showZero && balance === 0) return '$0.00';
  const output = dollarNumberWithCommasOrDash(balance || 0) || '-';
  if (amount && amount < 0) return `(${output})`;
  return output;
};

const getSectionRows = (
  section: SectionContainer,
  monthSummary: MonthSummary,
  cashBalanceRows: CashBalanceRow[],
  sectionTotal: number,
  runningTotal: number,
) => {
  switch (section.type) {
    case 'lineItemsContainer':
      return (
        <>
          <Tr key={section.title} fontWeight="bold">
            <Td>{section.title}</Td>
            {section.title === 'Available Amounts' && (
              <>
                <Td textAlign="center" />
                <Td textAlign="center">
                  {getFormattedAmount(sectionTotal, true)}
                </Td>
              </>
            )}
            {section.title !== 'Available Amounts' && (
              <>
                <Td textAlign="center">
                  {getFormattedAmount(sectionTotal, true)}
                </Td>
                <Td textAlign="center">
                  {getFormattedAmount(runningTotal, true)}
                </Td>
              </>
            )}
          </Tr>
          {section.lineItems.map((lineItem) => (
            <>
              <Tr key={`${section.title}-line-items`}>
                <Td whiteSpace="pre">
                  {'    '}
                  {lineItem.tooltip && (
                    <Tooltip
                      label={lineItem.tooltip}
                      fontSize="md"
                      placement="bottom"
                      hasArrow
                    >
                      {lineItem.name}
                    </Tooltip>
                  )}
                  {!lineItem.tooltip && lineItem.name}
                </Td>
                {section.title === 'Available Amounts' && (
                  <>
                    <Td textAlign="center" />
                    <Td textAlign="center">
                      {getFormattedAmount(
                        getLineItemBalance(
                          lineItem,
                          monthSummary,
                          cashBalanceRows,
                        ),
                      )}
                    </Td>
                  </>
                )}
                {section.title !== 'Available Amounts' && (
                  <>
                    <Td textAlign="center">
                      {getFormattedAmount(
                        getLineItemBalance(
                          lineItem,
                          monthSummary,
                          cashBalanceRows,
                        ),
                      )}
                    </Td>
                    <Td textAlign="center" />
                  </>
                )}
              </Tr>
            </>
          ))}
        </>
      );
    case 'summaryAccrualContainer':
      return (
        <>
          <Tr key={section.title} fontWeight="bold">
            <Td>
              {section.tooltip && (
                <Tooltip
                  label={section.tooltip}
                  placement="bottom-start"
                  hasArrow
                >
                  {section.title}
                </Tooltip>
              )}
              {!section.tooltip && section.title}
            </Td>
            <Td textAlign="center">
              {getFormattedAmount(monthSummary[section.account])}
            </Td>
            <Td textAlign="center">{getFormattedAmount(runningTotal, true)}</Td>
          </Tr>
        </>
      );
    case 'summaryCashContainer':
      return (
        <>
          <Tr key={section.title} fontWeight="bold">
            <Td>
              {section.tooltip && (
                <Tooltip
                  label={section.tooltip}
                  placement="bottom-start"
                  hasArrow
                >
                  {section.title}
                </Tooltip>
              )}
              {!section.tooltip && section.title}
            </Td>
            <Td textAlign="center">
              {getFormattedAmount(section.amount, true)}
            </Td>
            <Td textAlign="center">{getFormattedAmount(runningTotal, true)}</Td>
          </Tr>
        </>
      );
    default:
      throw Error('unknown line item type');
  }
};

// Define hardcoded structure for DEMO version of table setup
// Fill in accrual values if they are provided as input
type MonthlySummaryTableBodyProps = {
  formattedMonth: string;
  monthSummary: MonthSummary;
  cashBalances: CashBalanceRow[]; // TODO - add from container
  demoSections: DemoSectionFormat;
};
const MonthlySummaryTableBody = ({
  formattedMonth,
  monthSummary,
  cashBalances,
  demoSections,
}: MonthlySummaryTableBodyProps) => {
  let runningTotal = 0;
  return (
    <Tbody>
      <Tr>
        <Td fontWeight="bold" fontSize="2xl">
          Waterfall Report for {formattedMonth}
        </Td>
        <Td textAlign="center" fontWeight="bold">
          Owed
        </Td>
        <Td textAlign="center" fontWeight="bold">
          Remaining
        </Td>
      </Tr>
      {demoSections.map((section) => {
        // Collect total amount for section
        const sectionTotal = getSectionTotal(
          section,
          monthSummary,
          cashBalances,
        );
        if (section.title === 'Available Amounts') {
          runningTotal += sectionTotal;
        } else {
          runningTotal -= sectionTotal;
        }

        return getSectionRows(
          section,
          monthSummary,
          cashBalances,
          sectionTotal,
          runningTotal,
        );
      })}
    </Tbody>
  );
};

export const MonthlySummaryTable = ({
  monthFormatted,
  monthSummary,
  cashBalances,
}: {
  monthFormatted: string;
  monthSummary: MonthSummary;
  cashBalances: CashBalanceRow[];
}) => (
  <>
    <Table background={colors.millet[50]}>
      <Thead />
      <MonthlySummaryTableBody
        formattedMonth={monthFormatted}
        monthSummary={monthSummary}
        cashBalances={cashBalances}
        demoSections={monthlySummaryDemoSections}
      />
    </Table>
  </>
);
